/**
 * Clinical score rules (legacy app default)
 *
 * Classifies global sagittal compensation by scoring evidence for Swayback vs Anterior Pelvic Tilt
 * from lateral metric strings: pelvic tilt magnitude/direction, hip vs shoulder anterior shift,
 * knee state (flexion vs hyperextension), and torso sway (shoulder–hip offset relative to ankle plumb).
 * Uses YAML thresholds from evaluation-rules (sex-specific pelvic tilt minimum, hip forward cm, etc.).
 * ASIS/PSIS improve pelvic tilt accuracy when present (via serialized “Pelvic Tilt (ASIS-PSIS)” string).
 */

import { getEvaluationRules } from '../../evaluation-rules-load';
import type { PostureOrientationClassificationStrategy } from './posture-orientation-strategy-interface';
import type {
  PostureClassificationStrategyContext,
  PostureClassificationStrategyInput,
  PostureClassificationStrategyResult,
  PostureOrientationLabel,
} from './posture-orientation-strategy-types';
import { parseLateralPostureMetrics } from './posture-analysis-metrics-parse';
import {
  buildOrientationUiMappedInputs,
  CLINICAL_APT_POSSIBLE_SIGNALS,
  CLINICAL_SB_POSSIBLE_SIGNALS,
  CLINICAL_SCORE_SIGNAL_UI_MAP,
  logOrientationDecisionDebug,
  logPostureClassificationDebug,
} from './posture-classification-debug';

const STRATEGY_ID = 'clinicalScoreRules';
const DEBUG_LOG_PREFIX = '[clinicalScoreRules]';
const STRATEGY_NAME = 'Clinical score rules (legacy default)';

/** Max points if both sides fully fire all APT and SB branches (theoretical cap for confidence denominator). */
const MAX_POINTS = 10;

export const clinicalScoreRulesStrategy: PostureOrientationClassificationStrategy = {
  id: STRATEGY_ID,
  displayName: STRATEGY_NAME,
  maxClassificationPoints: MAX_POINTS,

  classify(
    input: PostureClassificationStrategyInput,
    context: PostureClassificationStrategyContext
  ): PostureClassificationStrategyResult | null {
    const rules = (getEvaluationRules() as any).posture.thresholds;
    const parsed = parseLateralPostureMetrics(input.analysisData, {
      kneeFlexionDegMin: rules.kneeFlexionDegMin,
      kneeHyperextensionDegMin: rules.kneeHyperextensionDegMin,
    });
    if (!parsed) return null;

    const STRICT_TILT = rules.strictTilt;
    const STRICT_HIP = rules.strictHip;
    const STRICT_LEAN = rules.strictLean;
    const pelvicTiltDegMin =
      input.clientSex === 'Female' ? rules.pelvicTiltDegMinFemale : rules.pelvicTiltDegMinMale;
    const hipForwardCmMin = rules.hipForwardCmMin;
    const torsoLeanDegMin = rules.trunkLeanLatDegMin;

    const pelvicTilt = parsed.pelvicTilt;
    const hipFwdVal = parsed.hipFwdVal;
    const shoFwdVal = parsed.shoFwdVal;
    const sagittalTorsoOffset = parsed.sagittalTorsoOffset;
    const kneeFlexVal = parsed.maxKneeFlexDeg;
    const kneeHyperextVal = parsed.maxKneeHyperextDeg;
    const {
      isKneeFlexed,
      isKneeHyperext,
      isKneeNeutral,
    } = parsed;

    logPostureClassificationDebug(
      DEBUG_LOG_PREFIX,
      'classification inputs (UI-mapped)',
      buildOrientationUiMappedInputs({
        analysisData: input.analysisData,
        parsedViewSide: parsed.viewSide,
        clientSex: input.clientSex,
        fromLandmarks: false,
        hipAnteriorShiftCm: hipFwdVal,
        shoulderAnteriorShiftCm: shoFwdVal,
        shoulderMinusHipOffsetCm: sagittalTorsoOffset,
        kneeHyperextension: isKneeHyperext && !isKneeNeutral,
        kneeFlexion: isKneeFlexed && !isKneeNeutral ? true : null,
        pelvicTiltDeg: pelvicTilt,
        parsed,
        pelvicTiltDegMin,
        rules,
        kneeHyperextensionDegMin: rules.kneeHyperextensionDegMin,
        hasAsisPsisLine: !!context.asisPsisLine,
        frontalViewForContext: context.frontalView,
      })
    );

    const display = (getEvaluationRules() as any).posture.display;
    let patternReason: string[] = [];
    const activeSignals: string[] = [];

    let aptScore = 0;
    let sbScore = 0;
    let aptTendency = false;
    let sbTendency = false;

    if (isFinite(pelvicTilt)) {
      if (pelvicTilt >= pelvicTiltDegMin) {
        aptScore += 3;
        activeSignals.push('clinical.apt.pelvis.anteriorTilt');
        patternReason.push(
          `Anterior Pelvic Tilt ${pelvicTilt.toFixed(1)}° (Clinical ≥${pelvicTiltDegMin}°)`
        );
        aptTendency = true;
      } else if (pelvicTilt >= STRICT_TILT) {
        activeSignals.push('clinical.apt.pelvis.mildAnterior');
        patternReason.push(`Mild Anterior Pelvic Tilt ${pelvicTilt.toFixed(1)}° (>${STRICT_TILT}°)`);
      }
    }

    if (shoFwdVal > STRICT_HIP && shoFwdVal > hipFwdVal + STRICT_LEAN) {
      aptScore += 2;
      activeSignals.push('clinical.apt.globalForwardLean');
      patternReason.push(`Global Forward Lean (Shoulders Ant ${shoFwdVal.toFixed(1)}cm)`);
      if (isFinite(pelvicTilt) && pelvicTilt >= pelvicTiltDegMin) aptTendency = true;
    }

    if (isKneeHyperext && !isKneeNeutral) {
      aptScore += 1;
      activeSignals.push('clinical.apt.knee.hyperextension');
      patternReason.push(
        isFinite(kneeHyperextVal)
          ? `Knee Hyperextension ${kneeHyperextVal.toFixed(0)}°`
          : `Knee Hyperextension`
      );
      if (isFinite(pelvicTilt) && pelvicTilt >= pelvicTiltDegMin) aptTendency = true;
    }
    if (sagittalTorsoOffset > torsoLeanDegMin) {
      aptScore += 1;
      activeSignals.push('clinical.apt.torso.forward');
      patternReason.push(`Forward Torso Lean`);
      if (isFinite(pelvicTilt) && pelvicTilt >= pelvicTiltDegMin) aptTendency = true;
    } else if (sagittalTorsoOffset > STRICT_LEAN) {
      activeSignals.push('clinical.apt.torso.forwardMild');
      patternReason.push(`Mild Forward Torso Lean`);
      if (isFinite(pelvicTilt) && pelvicTilt >= pelvicTiltDegMin) aptTendency = true;
    }

    const isLeaningForward = sagittalTorsoOffset > STRICT_LEAN;
    if (!isLeaningForward) {
      if (hipFwdVal > hipForwardCmMin) {
        sbScore += 2;
        activeSignals.push('clinical.sb.hip.markedAnterior');
        patternReason.push(`Hip Anterior Shift ${hipFwdVal.toFixed(1)}cm (Clinical >${hipForwardCmMin}cm)`);
        sbTendency = true;
      } else if (hipFwdVal > STRICT_HIP) {
        activeSignals.push('clinical.sb.hip.mildAnterior');
        patternReason.push(`Mild Hip Anterior Shift ${hipFwdVal.toFixed(1)}cm (>${STRICT_HIP}cm)`);
        sbTendency = true;
      }
    }

    if (isKneeFlexed && !isKneeNeutral) {
      sbScore += 2;
      activeSignals.push('clinical.sb.knee.flexion');
      patternReason.push(
        isFinite(kneeFlexVal) ? `Knee Flexion ${kneeFlexVal.toFixed(0)}°` : `Knee Flexion`
      );
      sbTendency = true;
    }

    const isRibcageBehindPelvis = sagittalTorsoOffset < -torsoLeanDegMin;
    if (isRibcageBehindPelvis) {
      sbScore += 1;
      activeSignals.push('clinical.sb.torso.sway');
      patternReason.push(`Torso Sway (Ribcage behind Pelvis)`);
      sbTendency = true;
    } else if (sagittalTorsoOffset < -STRICT_LEAN) {
      activeSignals.push('clinical.sb.torso.swayMild');
      patternReason.push(`Mild Torso Sway`);
      sbTendency = true;
    }

    const aptRatio = isFinite(pelvicTilt) ? Math.max(0, pelvicTilt) / pelvicTiltDegMin : 0;
    const sbRatio = hipFwdVal / hipForwardCmMin;

    let displayPattern = display.patterns.stacked as string;
    let orientation: PostureOrientationLabel = 'Stacked';
    let matchedPoints = 0;
    let decisionPath = 'stacked.default';

    if (aptTendency || sbTendency) {
      if (sbScore > aptScore) {
        displayPattern = display.patterns.sb;
        orientation = 'Swayback';
        matchedPoints = sbScore;
        decisionPath = 'swayback.sbScoreGreater';
      } else if (aptScore > sbScore) {
        displayPattern = display.patterns.apt;
        orientation = 'Anterior Pelvic Tilt';
        matchedPoints = aptScore;
        decisionPath = 'apt.aptScoreGreater';
      } else {
        if (sbRatio > aptRatio && (hipFwdVal > STRICT_HIP || isRibcageBehindPelvis)) {
          displayPattern = display.patterns.sb;
          orientation = 'Swayback';
          matchedPoints = sbScore;
          decisionPath = 'tie.sbRatioWins';
        } else {
          displayPattern = display.patterns.apt;
          orientation = 'Anterior Pelvic Tilt';
          matchedPoints = aptScore;
          decisionPath = 'tie.aptRatioOrDefault';
        }
      }
    } else {
      displayPattern = 'Stacked';
      orientation = 'Stacked';
      patternReason.push('Ribcage Aligned over Pelvis');
      patternReason.push('No Significant Deviations Detected');
      matchedPoints = 0;
    }

    let forwardLeanSubtype = false;
    if (displayPattern === display.patterns.apt && sagittalTorsoOffset > torsoLeanDegMin) {
      forwardLeanSubtype = true;
      patternReason.push(`APT subtype: Forward Lean (Shoulders ahead of hips)`);
    }

    const confidencePct = Math.min(100, Math.round((matchedPoints / MAX_POINTS) * 100));

    logOrientationDecisionDebug(DEBUG_LOG_PREFIX, {
      signalUiMap: CLINICAL_SCORE_SIGNAL_UI_MAP,
      activeSignals,
      signalGroups: [
        { label: 'clinical.swayback', prefix: 'clinical.sb.', possible: CLINICAL_SB_POSSIBLE_SIGNALS },
        { label: 'clinical.apt', prefix: 'clinical.apt.', possible: CLINICAL_APT_POSSIBLE_SIGNALS },
      ],
      scoreByGroup: {
        'clinical.swayback': { pts: sbScore },
        'clinical.apt': { pts: aptScore },
      },
      decisionPath,
      orientation,
      matchedPoints,
      maxPoints: MAX_POINTS,
      tieInputs:
        aptScore === sbScore && (aptTendency || sbTendency)
          ? { aptRatio, sbRatio, hipFwdVal, hipForwardCmMin, isRibcageBehindPelvis }
          : null,
      finalEvidence: patternReason,
    });

    return {
      orientation,
      confidencePct:
        orientation === 'Stacked'
          ? Math.min(100, Math.round((1 - matchedPoints / Math.max(1, MAX_POINTS)) * 100))
          : confidencePct,
      matchedPoints,
      maxPoints: MAX_POINTS,
      strategyId: STRATEGY_ID,
      strategyDisplayName: STRATEGY_NAME,
      evidence: patternReason,
      displayPattern: forwardLeanSubtype ? `${display.patterns.apt} (Forward Lean)` : displayPattern,
      forwardLeanSubtype,
    };
  },
};
